import {
  EntityItemType as EntityType,
  ImageMetadata,
  VideoMetadata,
  ProductContentAccuracyRecord
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/ContentServerProxy/types';
import { AccuracyData, CharacterAccuracyData, ChartDataAdapter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/types';
import { safeDivide } from 'src/utils/app';

type EntityTypeToDataMap = {
  [key in 'Title' | 'Item Highlights' | 'Description' | 'Bullet' | 'Image' | 'Video']: AccuracyData[];
};

const TITLE_MAP = {
  [EntityType.Title]: 'Title',
  [EntityType.ItemHighlights]: 'Item Highlights',
  [EntityType.BulletPointRecord]: 'Bullet',
  [EntityType.Description]: 'Description',
  [EntityType.ImageRecord]: 'Image',
  [EntityType.VideoRecord]: 'Video'
};

const MIN_REQUIRED_BULLETS = 5;
const MIN_REQUIRED_IMAGES = 6;
const MIN_REQUIRED_VIDEOS = 1;
const MIN_REQUIRED_DESCRIPTIONS = 1;
const MIN_REQUIRED_TITLE = 1;

export const getResolutionString = (metadata: ImageMetadata | VideoMetadata) => {
  const getUnit = (unit: string) => {
    switch (unit) {
      case 'px':
        return 'px';
      default:
        return '';
    }
  };

  return `${metadata?.width} x ${metadata?.height} ${getUnit(metadata?.unit)}`;
};

export const getRatioString = (metadata: ImageMetadata | VideoMetadata) => {
  const ratio = safeDivide(metadata?.width, metadata?.height);

  return ratio % 1 === 0 ? `${ratio}:1` : `${ratio.toFixed(3)}:1`;
};

export const getDurationString = (metadata: VideoMetadata) => {
  const totalSeconds = metadata?.duration;
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return `${minutes}:${seconds} seconds`;
};

class ProductContentAccuracyRecordAdapter {
  record: ProductContentAccuracyRecord;

  constructor(record: ProductContentAccuracyRecord) {
    this.record = record;
  }

  private createBaseAccuracy() {
    return {
      title: TITLE_MAP[this.record.entityType],
      isAccurate: this.record.isMatch
    };
  }

  private transformText() {
    return {
      ...this.createBaseAccuracy(),
      type: 'character' as const,
      currentContent: this.record.currentEntity?.entityValue,
      approvedContent: this.record.approvedEntity?.entityValue
    };
  }

  private transformImage() {
    return {
      ...this.createBaseAccuracy(),
      type: 'image' as const,
      currentContent: {
        url: this.record.currentEntity?.entityValue,
        altText: this.record.currentEntity?.entityValue,
        resolution: getResolutionString(this.record.currentEntity?.entityMetadata),
        ratio: getRatioString(this.record.currentEntity?.entityMetadata)
      },
      approvedContent: {
        url: this.record.approvedEntity?.entityValue,
        altText: this.record.approvedEntity?.entityValue,
        resolution: getResolutionString(this.record.approvedEntity?.entityMetadata),
        ratio: getRatioString(this.record.approvedEntity?.entityMetadata)
      }
    };
  }

  private transformVideo() {
    return {
      ...this.createBaseAccuracy(),
      type: 'video' as const,
      currentContent: this.record.currentEntity
        ? {
          url: this.record.currentEntity.entityValue,
          resolution: getResolutionString(this.record.currentEntity.entityMetadata),
          length: getDurationString(this.record.currentEntity.entityMetadata as VideoMetadata)
        }
        : {
          url: null,
          resolution: null,
          length: null
        },
      approvedContent: {
        url: this.record.approvedEntity?.entityValue,
        resolution: getResolutionString(this.record.approvedEntity?.entityMetadata),
        length: getDurationString(this.record.approvedEntity?.entityMetadata as VideoMetadata)
      }
    };
  }

  toAccuracyData(): AccuracyData {
    switch (this.record.entityType) {
      case EntityType.Title:
      case EntityType.ItemHighlights:
      case EntityType.BulletPointRecord:
      case EntityType.Description:
        return this.transformText();
      case EntityType.ImageRecord:
        return this.transformImage();
      case EntityType.VideoRecord:
        return this.transformVideo();
      default:
        return null;
    }
  }
}

export class ProductContentAccuracyAdapter implements ChartDataAdapter<AccuracyData[]> {
  data: ProductContentAccuracyRecord[];

  includeItemHighlights: boolean;

  constructor(data: ProductContentAccuracyRecord[], includeItemHighlights = false) {
    this.data = data;
    this.includeItemHighlights = includeItemHighlights;
  }

  transform(): AccuracyData[] {
    const includeItemHighlights = this.includeItemHighlights;
    const handleMissingMasterContent = (
      data: AccuracyData,
      title: 'description' | 'image' | 'video' | 'bullet' | 'title' | 'item highlights'
    ): AccuracyData => {
      if (data.approvedContent === undefined) {
        return {
          title: data.title,
          approvedContent: 'Master content is missing. Please define or contact support for assistance.',
          currentContent: `We are unable to measure the accuracy of this ${title}. Please define your master content.`,
          isAccurate: null,
          type: 'character'
        };
      }

      return data;
    };

    const getPlaceholderContent = (
      title: 'Description' | 'Image' | 'Video' | 'Title' | 'Item Highlights'
    ): AccuracyData => {
      if (title === 'Image' || title === 'Video') {
        return {
          title,
          approvedContent: null,
          currentContent: null,
          isAccurate: false,
          type: title === 'Image' ? 'image' : 'video'
        };
      }
      const suffix = ['Description', 'Title', 'Item Highlights'].includes(title) ? '' : 's';
      const content = `Missing required ${title.toLowerCase()}${suffix}.`;
      return {
        title,
        approvedContent: content,
        currentContent: content,
        isAccurate: false,
        type: 'character'
      };
    };

    const getMissingRequiredContent = (
      title: 'Image' | 'Video' | 'Bullet' | 'Title' | 'Description' | 'Item Highlights'
    ): AccuracyData => {
      if (title === 'Image' || title === 'Video') {
        return {
          title,
          approvedContent: null,
          currentContent: null,
          isAccurate: false,
          type: title === 'Image' ? 'image' : 'video'
        };
      }

      return {
        title,
        approvedContent: `Missing required ${title.toLowerCase()}.`,
        currentContent: `Missing required ${title.toLowerCase()}.`,
        isAccurate: false,
        type: 'character'
      };
    };

    const fillMissingDescription = (data: AccuracyData[]) => {
      const numDescriptions = data.length;

      if (numDescriptions === 0) {
        return [getPlaceholderContent('Description')];
      }

      return [...Array(Math.max(numDescriptions, MIN_REQUIRED_DESCRIPTIONS))].map((_, index) => {
        if (index < numDescriptions) {
          return handleMissingMasterContent(data[index], 'description');
        } else {
          return getMissingRequiredContent('Description');
        }
      });
    };

    const fillMissingTitle = (data: AccuracyData[]) => {
      const numTitles = data.length;

      if (numTitles === 0) {
        return [getPlaceholderContent('Title')];
      }

      return [...Array(Math.max(numTitles, MIN_REQUIRED_TITLE))].map((_, index) => {
        if (index < numTitles) {
          return handleMissingMasterContent(data[index], 'title');
        } else {
          return getMissingRequiredContent('Title');
        }
      });
    };

    const fillMissingItemHighlights = (data: AccuracyData[]) => {
      if (data.length === 0) {
        return [getPlaceholderContent('Item Highlights')];
      }

      return data.map((item) => handleMissingMasterContent(item, 'item highlights'));
    };

    /**
     * Fills in missing bullets with placeholders.
     *
     * @returns an array of length >= MIN_REQUIRED_BULLETS
     */
    const fillMissingBullets = (data: AccuracyData[]) => {
      const numBullets = data.length;

      return [...Array(Math.max(numBullets, MIN_REQUIRED_BULLETS))].map((_, index) => {
        if (index < numBullets) {
          return handleMissingMasterContent(data[index], 'bullet');
        } else {
          return getMissingRequiredContent('Bullet');
        }
      });
    };

    /**
     * Fills in missing videos or images with placeholders.
     *
     * @returns If data.length === 0, returns a single placeholder card. Otherwise, returns an array of length >= MIN_REQUIRED_IMAGES or MIN_REQUIRED_VIDEOS.
     */
    const fillMissingContent = (data: AccuracyData[], contentType: 'Image' | 'Video') => {
      const numItems = data?.length || 0;
      const minRequired = contentType === 'Image' ? MIN_REQUIRED_IMAGES : MIN_REQUIRED_VIDEOS;

      if (numItems === 0) {
        return [getPlaceholderContent(contentType)];
      }

      return [...Array(Math.max(numItems, minRequired))].map((_, index) => {
        if (index < numItems) {
          return handleMissingMasterContent(data[index], contentType.toLowerCase() as 'image' | 'video');
        } else {
          return getMissingRequiredContent(contentType);
        }
      });
    };

    const fillMissingImages = (data: AccuracyData[]) => fillMissingContent(data, 'Image');
    const fillMissingVideos = (data: AccuracyData[]) => fillMissingContent(data, 'Video');

    const groupDataByEntityType = (data: AccuracyData[]): EntityTypeToDataMap => {
      const groupedData = data.reduce(
        (acc, item) => {
          acc[item.title] = [...(acc[item.title] || []), item];
          return acc;
        },
        {
          Title: [],
          'Item Highlights': [],
          Description: [],
          Bullet: [],
          Image: [],
          Video: []
        }
      );

      return groupedData;
    };

    /**
     * Fills in missing content with placeholders.
     *
     * @returns an array with the missing content filled in.
     */
    const withPlaceholderContent = (data: AccuracyData[]) => {
      const { Title, Description, Bullet, Image, Video, ['Item Highlights']: ItemHighlights } =
        groupDataByEntityType(data);
      const title = fillMissingTitle(Title);
      const itemHighlights = includeItemHighlights ? fillMissingItemHighlights(ItemHighlights) : [];
      const description = fillMissingDescription(Description);
      const bullets = fillMissingBullets(Bullet);
      const images = fillMissingImages(Image);
      const videos = fillMissingVideos(Video);

      return [...title, ...itemHighlights, ...bullets, ...description, ...images, ...videos];
    };

    /**
     * Adds a number suffix to the title of bullets, images, and videos.
     *
     * @returns an array with the titles of bullets, images, and videos modified.
     */
    const withIndexedTitles = (data: AccuracyData[]) => {
      const indices = { Bullet: 1, Image: 1, Video: 1 };

      return data.map((item) => {
        if (['Bullet', 'Image', 'Video'].includes(item.title)) {
          const newItem = {
            ...item,
            title: `${item.title} ${indices[item.title]}`
          };
          indices[item.title]++;
          return newItem;
        }
        return item;
      });
    };

    const transformedData: AccuracyData[] = this.data
      .map((record) => {
        return new ProductContentAccuracyRecordAdapter(record).toAccuracyData();
      })
      .filter((item) => item !== null);

    return withIndexedTitles(withPlaceholderContent(transformedData));
  }
}
